// WordPress Sync Service - Stateless functions for fetching posts from WP REST API
// Separate from wordpressService.ts (which is stateful/singleton for publishing)

export interface WpPostRaw {
  id: number;
  title: { rendered: string };
  slug: string;
  status: string;
  excerpt: { rendered: string };
  content: { rendered: string };
  date: string;
  modified: string;
  link: string;
  acf?: Record<string, unknown>;
  _embedded?: {
    'wp:featuredmedia'?: Array<{ source_url?: string }>;
    'wp:term'?: Array<Array<{ id: number; name: string; taxonomy: string }>>;
  };
}

export interface WpFetchResult {
  posts: WpPostRaw[];
  totalPosts: number;
  totalPages: number;
}

export interface WpSyncCredentials {
  url: string;
  username: string;
  password: string;
}

function buildAuthHeaders(credentials: WpSyncCredentials): Record<string, string> {
  const encodedCredentials = btoa(`${credentials.username}:${credentials.password}`);
  return {
    'Authorization': `Basic ${encodedCredentials}`,
    'User-Agent': 'PBN-SD-Sync/1.0',
  };
}

/**
 * Extract keyword from ACF fields (if ACF plugin is installed on WP).
 * Zapisywane do wp_posts.keyword przy sync — uzywane jako metadata, nie w matcherze.
 */
export function extractKeyword(post: WpPostRaw): string {
  return (post.acf?.keyword as string) || '';
}

/**
 * Fetch a single page of posts from WP REST API.
 */
export async function fetchWpPostsPage(
  credentials: WpSyncCredentials,
  page: number = 1,
  perPage: number = 100,
): Promise<WpFetchResult> {
  const cleanUrl = credentials.url.replace(/\/$/, '');
  const params = new URLSearchParams({
    per_page: perPage.toString(),
    page: page.toString(),
    status: 'any',
    _embed: 'true',
    acf_format: 'standard',
    orderby: 'modified',
    order: 'desc',
  });

  const url = `${cleanUrl}/wp-json/wp/v2/posts?${params}`;

  const response = await fetch(url, {
    headers: buildAuthHeaders(credentials),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
  }

  const posts: WpPostRaw[] = await response.json();
  const totalPosts = parseInt(response.headers.get('X-WP-Total') || '0', 10);
  const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '0', 10);

  return { posts, totalPosts, totalPages };
}

/**
 * Fetch ALL posts from a WordPress site, handling pagination.
 */
export async function fetchAllWpPosts(
  credentials: WpSyncCredentials,
  onProgress?: (fetched: number, total: number) => void,
): Promise<WpPostRaw[]> {
  const allPosts: WpPostRaw[] = [];

  const firstPage = await fetchWpPostsPage(credentials, 1, 100);
  allPosts.push(...firstPage.posts);
  onProgress?.(allPosts.length, firstPage.totalPosts);

  for (let page = 2; page <= firstPage.totalPages; page++) {
    const result = await fetchWpPostsPage(credentials, page, 100);
    allPosts.push(...result.posts);
    onProgress?.(allPosts.length, firstPage.totalPosts);
  }

  return allPosts;
}

/**
 * Extract category/tag names from embedded WP response.
 */
export function extractTerms(
  post: WpPostRaw,
  taxonomy: 'category' | 'post_tag'
): Array<{ id: number; name: string }> {
  if (!post._embedded?.['wp:term']) return [];

  for (const termGroup of post._embedded['wp:term']) {
    const filtered = termGroup.filter(t => t.taxonomy === taxonomy);
    if (filtered.length > 0) {
      return filtered.map(t => ({ id: t.id, name: t.name }));
    }
  }
  return [];
}

/**
 * Extract featured image URL from embedded WP response.
 */
export function extractFeaturedImage(post: WpPostRaw): string | null {
  return post._embedded?.['wp:featuredmedia']?.[0]?.source_url || null;
}

/**
 * Compute SHA-256 hash of content for change detection.
 */
export async function computeContentHash(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Strip HTML tags for clean excerpt display.
 */
export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}
